import { supabase } from '../../config/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { User, AuthResponse, LoginCredentials, RegisterData } from '../../models/User';
import { BaseApi } from './BaseApi';
import { Logger } from '../../utils/logger';
import { SingaporeTime } from '../../utils/unified-date';
import { getErrorMessage, toError } from '../../utils/errorMessage';

/**
 * API service for handling authentication operations with Supabase
 */
export class AuthApi extends BaseApi {
  /**
   * Sign in with email and password
   */
  static async signIn(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) throw error;

      // Get user data from users table
      const userData = await this.getUserData(data.user?.id);

      return {
        user: userData,
        session: data.session,
        error: null,
      };
    } catch (err: unknown) {
      Logger.serviceError('AuthApi', 'signIn', toError(err), {
        email: credentials.email,
        operation: 'sign_in_catch',
      });
      return {
        user: null,
        session: null,
        error: getErrorMessage(err, 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Sign up new user
   */
  static async signUp(registerData: RegisterData): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            name: registerData.name,
            role: registerData.role || 'worker',
          },
        },
      });

      if (error) throw error;

      // Create profile record
      if (data.user) {
        const { error: profileError } = await supabase.from('users').insert({
          id: data.user.id,
          name: registerData.name,
          email: registerData.email,
          role: registerData.role || 'worker',
          is_active: true, // New users are active by default
          created_at: SingaporeTime.now(),
          updated_at: SingaporeTime.now(),
        });

        if (profileError) throw profileError;
      }

      return {
        user: data.user
          ? {
              id: data.user.id,
              email: data.user.email || '',
              name: registerData.name,
              role: registerData.role || 'worker',
              created_at: data.user.created_at || SingaporeTime.now(),
              updated_at: data.user.updated_at || SingaporeTime.now(),
            }
          : null,
        session: data.session,
        error: null,
      };
    } catch (err: unknown) {
      Logger.serviceError('AuthApi', 'signUp', toError(err), {
        email: registerData.email,
        operation: 'sign_up_catch',
      });
      return {
        user: null,
        session: null,
        error: getErrorMessage(err, 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Sign out current user
   */
  static async signOut(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (err: unknown) {
      Logger.serviceError('AuthApi', 'signOut', toError(err), {
        operation: 'sign_out_catch',
      });
      return { error: getErrorMessage(err, 'An unexpected error occurred') };
    }
  }

  /**
   * Send a password reset email that links back to /auth/reset-password
   */
  static async sendPasswordResetEmail(email: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      return { error: null };
    } catch (err: unknown) {
      Logger.serviceError('AuthApi', 'sendPasswordResetEmail', toError(err), {
        operation: 'send_password_reset_catch',
      });
      return { error: getErrorMessage(err, 'An unexpected error occurred') };
    }
  }

  /**
   * Set a new password for the signed-in (recovery-session) user
   */
  static async updatePassword(password: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      return { error: null };
    } catch (err: unknown) {
      Logger.serviceError('AuthApi', 'updatePassword', toError(err), {
        operation: 'update_password_catch',
      });
      return { error: getErrorMessage(err, 'An unexpected error occurred') };
    }
  }

  /**
   * Get current session and user
   */
  static async getCurrentSession(): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;

      if (!data.session) {
        return { user: null, session: null, error: null };
      }

      // Get user data from users table
      const userData = await this.getUserData(data.session.user.id);

      return {
        user: userData,
        session: data.session,
        error: null,
      };
    } catch (err: unknown) {
      Logger.serviceError('AuthApi', 'getCurrentSession', toError(err), {
        operation: 'get_current_session_catch',
      });
      return {
        user: null,
        session: null,
        error: getErrorMessage(err, 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Get user data from users table
   */
  private static async getUserData(userId?: string): Promise<User | null> {
    if (!userId) return null;

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (userError) {
      Logger.serviceError('AuthApi', 'getUserData', toError(userError), {
        userId,
        operation: 'get_user_data_error',
      });
      // Rethrow: a signed-in session without its profile row is an error the
      // caller must see, not a silent user:null "success".
      throw userError;
    }

    return userData;
  }

  /**
   * Create a user through the create-user Edge Function (manager-only,
   * service role on the server side). Unlike signUp, this does NOT swap the
   * caller's session and lets a manager create managers/sellers.
   */
  static async createUser(payload: {
    name: string;
    email: string;
    password: string;
    role: 'manager' | 'seller' | 'worker';
  }): Promise<{
    user: { id: string; email: string; name: string; role: string } | null;
    error: string | null;
  }> {
    const { data, error } = await supabase.functions.invoke('create-user', {
      body: payload,
    });

    if (error) {
      // FunctionsHttpError carries the function's JSON error body.
      let message = 'Failed to create user';
      if (error instanceof FunctionsHttpError) {
        const body = (await error.context.json().catch(() => null)) as {
          error?: string;
        } | null;
        message = body?.error ?? message;
      }
      return { user: null, error: message };
    }

    return { user: data?.user ?? null, error: null };
  }
}
